import { ContentNuggetBookmark } from './nugget';

export const BACKRS_ROLES = {
  STUDENT: 'Student',
  FOUNDATIONS: 'Foundations',
  PROFESSIONAL: 'Professional',
  VALIDATED_USER: 'Validated User',
  SCHOLAR: 'Scholar',
  SERVICE_PROVIDER: 'Service Provider',
  BACKRS_COACH: 'Backrs Coach',
  USER: 'User',
  PRIVILEGED_ADMIN: 'Privileged Admin',
  ADMIN_USER: 'Admin User',
  LEGACY_PROTEGE: 'Legacy Protege',
  LEGACY_BACKR: 'Legacy Backr',
  PROSPER_USER: 'Prosper User',
  LUBAV_USER: 'Lubav User',
  LUBAV_BETA: 'Lubav Beta',
  LUBAV_ALPHA: 'Lubav Alpha',
  ROLE_MODEL: 'Role Model',
  SUPER_COACH: 'Super Coach',
  INSTITUTION_ADMIN: 'Institution Admin',
} as const;

export type BackrsRole = (typeof BACKRS_ROLES)[keyof typeof BACKRS_ROLES];

export type UserRoles = Readonly<{
  id: number;
  name: BackrsRole;
}>;

/**
 * The base user model to be used across the monorepo. Extend this into a custom model per application, putting and shared properties here.
 */
export type UserDocuments = Readonly<{
  resume: boolean;
  transcript: boolean;
  personal_assessment: boolean;
  other: boolean;
}>;

export type BackrsUser = Readonly<{
  id: number;
  access_token: string;
  email: string;
  phone?: string;
  first_name: string;
  last_name: string;
  profile_image?: string;
  profile_cover_image?: string;

  /**
   * MM-DD-YYYY is format sent by the API
   */
  birthday?: string;

  zip_code?: string;

  location?: string;
  job_title?: string;
  employer?: string;
  school?: string;
  grade?: string;
  graduationYear?: number;
  enrollmentType?: string;
  role?: 'professional' | 'student' | 'foundations';
  bio?: string;
  roles?: UserRoles[];
  user_response_set_id?: string;
  bookmarks?: ContentNuggetBookmark[];

  isUnauthenticated?: boolean;
  hasDocument?: UserDocuments;
  has_blueprint?: boolean;
  has_track_assessment?: boolean;
  has_life_readiness?: boolean;

  password?: string; // only used for PUT
}>;

export const REMOTE_PERMISSIONS = {
  DIRECT_MESSAGE_COMMUNITY: 'direct_message_community',
  DIRECT_MESSAGE_PEERS: 'direct_message_peers',
  DIRECT_MESSAGE_AS_SYSTEM: 'direct_message_as_system',
  CRUD_POSTS: 'crud_posts',
  JOIN_NETWORKS: 'join_networks',
  INVEST_IN_GENERAL: 'invest_in_general',
  INIT_MATCH_RUN: 'init_match_run',
  INIT_BACKGROUND_CHECK: 'init_background_check',
  CRUD_GOALS: 'crud_goals',
  ACCESS_RESOURCE_LIBRARY: 'access_resource_library',
  ACCESS_PROSPER: 'access_prosper',
  VIEW_ALL_TEAMS: 'view_all_teams',
  MEET_PEERS: 'meet_peers',
  LUBAV_SUPER_COACH: 'lubav_super_coach',
  ACCESS_OWN_PROFILE: 'access_own_profile',
  GET_SUPPORT: 'get_support',
  CAN_INVEST: 'can_invest',
  DIRECT_MESSAGING: 'direct_messaging',
  ACCESS_SUPER_COACH: 'access_super_coach',
  EMULATE_USERS: 'emulate_users',
  ACCESS_GROW: 'access_grow',
  HAS_FAMILY_ACCESS: 'has_family_access',
  ACCESS_COACH_DASHBOARD: 'access_coach_dashboard',
  ACCESS_COACH_DASHBOARD_LUBAV: 'access_coach_dashboard_lubav',
  ACCESS_NETWORKS: 'access_networks',
  ACCESS_DEEP_DIVES: 'access_deep_dives',
  ACCESS_COACHES: 'access_coaches',
  ACCESS_COACH_ASSIGNEES: 'access_coach_assignees',
  ACCESS_COHORT_ANALYTICS: 'access_cohort_analytics',
  MANAGE_INSTITUTION_MEMBERS: 'manage_institution_members',
  ACCESS_OPERATOR_FLEET: 'access_operator_fleet',
  LUBAV_BETA: 'lubav_beta',
} as const;

export type RemotePermission = (typeof REMOTE_PERMISSIONS)[keyof typeof REMOTE_PERMISSIONS];

export type PermissionStatus = Readonly<{
  allowed: boolean;
  reason?: string;
  message?: string;
}>;

export type UserPermissions = Readonly<{
  permissions: Readonly<Record<RemotePermission, PermissionStatus>>;
}>;

export type ProfileOptionAnswer = Readonly<{
  id?: number;
  user_id: BackrsUser['id'];
  category_id: string;
  question_id: string;

  option_id?: string;
  suboption_id?: string;
  value?: string | null;
  source?: string;

  created_at?: string;
  updated_at?: string;
}>;
